import { get, post, notifyError } from 'services/request';
import { IBrowserData } from './types';

// The concept-tree endpoints live under /mcp (shared with the AI Assistant tools).
const BASE = 'mcp/concept-tree';

export const fetchBrowserData = async (courseId: number): Promise<IBrowserData | null> => {
  const data = await get<IBrowserData & { error?: string }>(`${BASE}/browser`, { courseId });
  if (!data || data.error || !data.concepts) {
    if (data?.error) notifyError(data.error);
    return null;
  }
  return data;
};

type IMcpResult = { error?: string } & { [key: string]: unknown };

const runAction = async (path: string, payload: object): Promise<boolean> => {
  const result = await post<IMcpResult>(`${BASE}/${path}`, payload);
  const error = result.data?.error;
  if (error) {
    notifyError(String(error));
    return false;
  }
  return true;
};

export const addRequirement = (courseId: number, code: string, requiresCode: string, note?: string) =>
  runAction('requirement/add', { courseId, code, requiresCode, note });

export const removeRequirement = (courseId: number, code: string, requiresCode: string) =>
  runAction('requirement/remove', { courseId, code, requiresCode });

export const setConceptLevel = (courseId: number, code: string, level: number, note?: string) =>
  runAction('level', { courseId, code, level, note });

export const dropConcept = (courseId: number, code: string, note?: string) => runAction('drop', { courseId, code, note });

export const restoreConcept = (courseId: number, code: string) => runAction('restore', { courseId, code });

export const updateConceptNote = (courseId: number, code: string, note: string) =>
  runAction('update', { courseId, code, note });

export const updateConceptTitle = (courseId: number, code: string, title: string) =>
  runAction('update', { courseId, code, title });

export const updateConceptGroup = (courseId: number, code: string, groupCode: string) =>
  runAction('update', { courseId, code, groupCode });

export const updateConceptText = (courseId: number, code: string, conceptText: string) =>
  runAction('update', { courseId, code, conceptText });

export const updateConceptExamples = (courseId: number, code: string, examples: string) =>
  runAction('update', { courseId, code, examples });

export const updateConceptRefLevel = (courseId: number, code: string, refLevel: number) =>
  runAction('update', { courseId, code, refLevel });

export const updateConceptKline = (courseId: number, code: string, klineId: number) =>
  runAction('update', { courseId, code, klineId });

export const deleteConcept = (courseId: number, code: string) => runAction('delete', { courseId, code });

export const unlinkConceptPanel = (courseId: number, code: string) => runAction('unlink-panel', { courseId, code });

export const linkConceptPanel = (courseId: number, code: string, panelId: number) =>
  runAction('link-panel', { courseId, code, panelId });

export const createConcept = (
  courseId: number,
  data: { code: string; title: string; level: number; klineId?: number; klinePos?: number; requires?: string },
) => runAction('create', { courseId, ...data });
